/**
 * InstanceTree - Self-contained tree dropdown for navigating graph instances
 *
 * Shows hierarchy of instances with definition type colors.
 * Uses ScopeContext for navigation and builds tree from definitionState.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useScope } from '../contexts/ScopeContext';
import { useRuntime } from '../contexts/RuntimeProvider';
import type { DefinitionStateMap } from '@/runtime/RuntimeStore';

// Colors for definition types (cycle through these)
const DEFINITION_COLORS = [
  '#60a5fa', // blue
  '#34d399', // green
  '#f472b6', // pink
  '#fbbf24', // yellow
  '#a78bfa', // purple
  '#fb923c', // orange
  '#2dd4bf', // teal
  '#f87171', // red
];

interface InstanceTreeNode {
  id: string;
  label?: string;
  path: string[];
  definitionId: string;
  children: InstanceTreeNode[];
}

/**
 * Recursively build a tree of all sub-graph instances from definitions
 */
function buildInstanceTree(
  definitionId: string,
  parentPath: string[],
  definitions: DefinitionStateMap
): InstanceTreeNode[] {
  const tree: InstanceTreeNode[] = [];
  const def = definitions[definitionId];
  if (!def) return tree;

  for (const [nodeId, node] of Object.entries(def.nodes)) {
    const nodeType = node.type;
    // Check if this node type is a sub-graph definition (exists in definitions and isn't a primitive)
    if (definitions[nodeType] && nodeType !== 'root') {
      const nodePath = [...parentPath, nodeId];
      tree.push({
        id: nodeId,
        label: node.label,
        path: nodePath,
        definitionId: nodeType,
        children: buildInstanceTree(nodeType, nodePath, definitions)
      });
    }
  }

  return tree;
}

export function InstanceTree() {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { path, setPath, isDefinitionEdit, definitionId: currentDefEditId } = useScope();
  const { definitionState } = useRuntime();

  // Build instance tree from root definition
  // Re-build when definition state changes (e.g., new sub-graph added)
  const instanceTree = useMemo(() => {
    if (!definitionState['root']) return [];
    return buildInstanceTree('root', ['root'], definitionState);
  }, [definitionState]);

  // Build color map for definitions (rebuild when tree changes)
  const definitionColorMap = useMemo(() => {
    const colorMap = new Map<string, string>();
    function assignColors(nodes: InstanceTreeNode[]) {
      for (const node of nodes) {
        if (!colorMap.has(node.definitionId)) {
          const colorIndex = colorMap.size % DEFINITION_COLORS.length;
          colorMap.set(node.definitionId, DEFINITION_COLORS[colorIndex]);
        }
        assignColors(node.children);
      }
    }
    assignColors(instanceTree);
    return colorMap;
  }, [instanceTree]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Get all non-root definitions for the Definitions section
  const availableDefinitions = useMemo(() => {
    return Object.keys(definitionState).filter(id => id !== 'root').sort();
  }, [definitionState]);

  // Find label for current path by traversing the tree
  const currentLabel = useMemo(() => {
    if (isDefinitionEdit) return currentDefEditId;
    if (path.length <= 1) return 'root';

    // Navigate through tree to find matching node
    let nodes = instanceTree;
    let label: string | undefined;
    for (let i = 1; i < path.length; i++) {
      const nodeId = path[i];
      const found = nodes.find(n => n.id === nodeId);
      if (found) {
        label = found.label;
        nodes = found.children;
      }
    }
    return label || path[path.length - 1];
  }, [path, instanceTree, isDefinitionEdit, currentDefEditId]);

  const isAtRoot = path.length <= 1 || (path.length === 1 && path[0] === 'root');

  const handleSelect = useCallback((newPath: string[]) => {
    setPath(newPath);
    setIsOpen(false);
  }, [setPath]);

  return (
    <div ref={containerRef} style={styles.container}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={styles.trigger}
      >
        <span>{currentLabel}</span>
        <span style={styles.arrow}>{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div style={styles.dropdown}>
          {/* Root item */}
          <div
            onClick={() => handleSelect(['root'])}
            style={{
              ...styles.item,
              background: isAtRoot ? '#374151' : 'transparent'
            }}
          >
            <span style={{ color: '#9ca3af' }}>root</span>
          </div>

          {/* Tree items */}
          {instanceTree.map(node => (
            <TreeNode
              key={node.path.join('/')}
              node={node}
              depth={0}
              currentPath={path}
              colorMap={definitionColorMap}
              onSelect={handleSelect}
            />
          ))}

          {/* Definitions section */}
          {availableDefinitions.length > 0 && (
            <>
              <div style={styles.separator}>
                <span style={{ color: '#6b7280', fontSize: 11 }}>Definitions</span>
              </div>
              {availableDefinitions.map(defId => {
                const isSelected = isDefinitionEdit && currentDefEditId === defId;
                const color = definitionColorMap.get(defId) || '#9ca3af';
                return (
                  <div
                    key={`def-${defId}`}
                    onClick={() => handleSelect(['~def', defId])}
                    style={{
                      ...styles.item,
                      background: isSelected ? '#374151' : 'transparent'
                    }}
                  >
                    <span style={{ color, marginRight: 6 }}>&#9672;</span>
                    <span style={{ color: 'white' }}>{defId}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface TreeNodeProps {
  node: InstanceTreeNode;
  depth: number;
  currentPath: string[];
  colorMap: Map<string, string>;
  onSelect: (path: string[]) => void;
}

function TreeNode({ node, depth, currentPath, colorMap, onSelect }: TreeNodeProps) {
  const isSelected = JSON.stringify(node.path) === JSON.stringify(currentPath);
  const color = colorMap.get(node.definitionId) || '#9ca3af';

  return (
    <>
      <div
        onClick={() => onSelect(node.path)}
        style={{
          ...styles.item,
          paddingLeft: 8 + depth * 16,
          background: isSelected ? '#374151' : 'transparent'
        }}
      >
        {/* Tree line */}
        <span style={{ color: '#4b5563', marginRight: 4 }}>
          {depth > 0 ? '└' : ''}
        </span>

        {/* Instance name (label if available, otherwise id) */}
        <span style={{ color: 'white', marginRight: 6 }}>
          {node.label || node.id}
        </span>

        {/* Definition type with color */}
        <span style={{ color, fontSize: 11 }}>
          {node.definitionId}
        </span>
      </div>

      {/* Children */}
      {node.children.map(child => (
        <TreeNode
          key={child.path.join('/')}
          node={child}
          depth={depth + 1}
          currentPath={currentPath}
          colorMap={colorMap}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative'
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#374151',
    color: 'white',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '4px 10px',
    fontSize: 13,
    cursor: 'pointer',
    minWidth: 100
  },
  arrow: {
    fontSize: 8,
    color: '#9ca3af'
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 4,
    background: '#1f2937',
    border: '1px solid #4b5563',
    borderRadius: 4,
    minWidth: 200,
    maxHeight: 300,
    overflowY: 'auto',
    zIndex: 100,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: 'monospace'
  },
  separator: {
    padding: '6px 8px 2px',
    borderTop: '1px solid #374151',
    marginTop: 4
  }
};
